// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: Claude Code and Codex updates on the person's own machines (konvo-agents 0.40.0+): what
// the machine's agent tool says about each CLI (installed version, newest one out, how an update
// from Konvo went), turned into the rows the Agents usage dock, machine menu and phone list show.

import type {AgentMachineView, AgentReadiness} from '@konvo/schema/src/domains/konvo/Agents';

export type AgentToolProvider = AgentReadiness['provider'];

/**
 * - ready: a newer version is out and Konvo can update it.
 * - manual: a newer version is out; the tool can't update it itself (or the machine is read-only), so the command shows.
 * - updating: queued or running on the machine.
 * - done: updated a moment ago.
 * - failed: the last update failed a while ago at most (`message` says why).
 */
export type AgentToolUpdatePhase = 'ready' | 'manual' | 'updating' | 'done' | 'failed';

export interface AgentToolUpdate {
	machineId: string;
	machineName: string;
	provider: AgentToolProvider;
	/** "Claude Code" or "Codex". */
	name: string;
	phase: AgentToolUpdatePhase;
	/** Installed version (before the update while it runs). */
	version: string | null;
	latestVersion: string | null;
	/** The command that updates it by hand, when the tool says. */
	command: string | null;
	/** Why the update failed, in the machine's (or Konvo's) words. */
	message: string | null;
	/** When the update finished (done and failed). */
	finishedAt: number | null;
}

/** How long "is up to date" stays after an update finished. */
export const AGENT_UPDATE_DONE_MS = 60_000;
/** How long a failed update keeps its reason (and Try again) before the plain "is out" row comes back. */
export const AGENT_UPDATE_FAILED_MS = 30 * 60_000;
/** An update asked for here shows as running until Konvo answers (its request gives up after 65 s). */
export const AGENT_UPDATE_PENDING_MS = 70_000;

/**
 * An update asked for from this app, by machine and provider, until the machine answers (then its
 * summary shows it running), or why asking failed. `at` is this device's clock.
 */
export interface AgentUpdateAsk {
	at: number;
	/** Set when the request failed: Konvo's or the machine's reason. */
	error?: string;
}

/**
 * When this app saw an update end (it was queued or running in an earlier summary), by machine and
 * provider, on this device's clock: "up to date" stays a minute from then, whatever the
 * machine's clock says.
 */
export interface AgentUpdateEnd {
	status: 'succeeded' | 'failed' | 'unchanged';
	at: number;
}

const ENDED = new Set(['succeeded', 'failed', 'unchanged']);

/** Updates that ended between two overviews: running (or asked for) before, over now. */
export function endedAgentUpdates(
	before: Array<AgentMachineView>,
	after: Array<AgentMachineView>,
	asks: ReadonlyMap<string, AgentUpdateAsk>,
	now: number,
): Array<[string, AgentUpdateEnd]> {
	const ended: Array<[string, AgentUpdateEnd]> = [];
	for (const machine of after) {
		const previous = before.find((entry) => entry.id === machine.id);
		for (const agent of machine.tools[0]?.agents ?? []) {
			const status = agent.update?.status;
			if (!status || !ENDED.has(status)) continue;
			const key = agentUpdateKey(machine.id, agent.provider);
			const was = previous?.tools[0]?.agents?.find((entry) => entry.provider === agent.provider)?.update?.status;
			if (was === 'queued' || was === 'running' || (asks.has(key) && !asks.get(key)?.error))
				ended.push([key, {status: status as AgentUpdateEnd['status'], at: now}]);
		}
	}
	return ended;
}

export const agentUpdateKey = (machineId: string, provider: string) => `${machineId}\u0000${provider}`;

export function agentToolName(provider: AgentToolProvider): string {
	return provider === 'codex' ? 'Codex' : 'Claude Code';
}

const time = (iso: string | undefined) => {
	const at = iso ? Date.parse(iso) : Number.NaN;
	return Number.isFinite(at) ? at : null;
};

/** One machine's update rows, Claude Code first. Empty when the bridge or tool reports no versions. */
export function agentToolUpdates(
	machine: AgentMachineView,
	now: number,
	asks: ReadonlyMap<string, AgentUpdateAsk> = new Map(),
	ends: ReadonlyMap<string, AgentUpdateEnd> = new Map(),
): Array<AgentToolUpdate> {
	if (!machine.online) return [];
	const tool = machine.tools[0];
	const oneClick = tool?.can.updateAgents === true && machine.limits.send;
	const rows: Array<AgentToolUpdate> = [];
	for (const agent of tool?.agents ?? []) {
		const update = agent.update;
		if (!update) continue;
		const key = agentUpdateKey(machine.id, agent.provider);
		const ask = asks.get(key);
		const seen = ends.get(key);
		const base = {
			machineId: machine.id,
			machineName: machine.name,
			provider: agent.provider,
			name: agentToolName(agent.provider),
			version: agent.version ?? null,
			latestVersion: agent.latestVersion ?? null,
			command: update.command ?? null,
			message: null,
			finishedAt: null,
		};
		if (
			update.status === 'queued' ||
			update.status === 'running' ||
			(ask && !ask.error && now - ask.at < AGENT_UPDATE_PENDING_MS)
		) {
			rows.push({...base, phase: 'updating'});
			continue;
		}
		if (ask?.error && now - ask.at < AGENT_UPDATE_FAILED_MS) {
			rows.push({...base, phase: 'failed', message: ask.error, finishedAt: ask.at});
			continue;
		}
		// Ended while this app watched: timed from when it saw that. Otherwise the machine's clock.
		const endedAt = seen && seen.status === update.status ? seen.at : time(update.finishedAt);
		if (update.status === 'succeeded' && endedAt != null && now - endedAt < AGENT_UPDATE_DONE_MS) {
			rows.push({...base, phase: 'done', finishedAt: endedAt});
			continue;
		}
		const failed = update.status === 'failed' || update.status === 'unchanged';
		if (failed && update.available && endedAt != null && now - endedAt < AGENT_UPDATE_FAILED_MS) {
			rows.push({...base, phase: 'failed', message: update.message ?? null, finishedAt: endedAt});
			continue;
		}
		if (!update.available) continue;
		rows.push({...base, phase: oneClick && update.canUpdate ? 'ready' : 'manual'});
	}
	return rows.sort((a, b) => (a.provider === b.provider ? 0 : a.provider === 'claudeAgent' ? -1 : 1));
}

/** Tools on the machine with a newer version out (the machine pill's "· 2 updates"). */
export function agentUpdatesWaiting(updates: Array<AgentToolUpdate>): number {
	return updates.filter((update) => update.phase !== 'done').length;
}

/** Every agent tool on the machine with its version, for the machine menu's "Agent tools on …". */
export function agentToolVersions(machine: AgentMachineView): Array<AgentReadiness> {
	return (machine.tools[0]?.agents ?? []).filter((agent) => agent.version);
}
